import { useEffect, useId, useRef, useState } from 'react';
import { useGameStore } from '../store/game-store.ts';
import { useLiveResources } from '../economy/live-store.ts';
import { formatCompact, formatEta, formatExact, formatRate, type LiveResources } from '../economy/live-resources.ts';

/**
 * The permanent stockpile bar under the main menu: visible on every tab (map, economy, research, fleets…).
 * It reads the one live store (economy/live-store), the same numbers every other view shows; the server stays
 * authoritative, the bar only previews `amount + rate × time` between snapshots.
 */

type Kind = 'metal' | 'energy' | 'rp';

interface ChipData {
  kind: Kind;
  icon: string;
  label: string;
  color: string;
  amount: number;
  rate: number;
  capacity: number | null;
  full: boolean;
  empty: boolean;
  warn: boolean;
}

function chipsOf(l: LiveResources): ChipData[] {
  const chips: ChipData[] = [
    { kind: 'metal', icon: '⚙️', label: 'Fém', color: '#9fb3c1', amount: l.metal, rate: l.metalPerHour, capacity: l.metalCapacity, full: l.metalFull, empty: l.metalEmpty, warn: l.metalPerHour < 0 },
    { kind: 'energy', icon: '⚡', label: 'Energia', color: '#ffd54f', amount: l.energy, rate: l.energyPerHour, capacity: l.energyCapacity, full: l.energyFull, empty: l.energyEmpty, warn: l.energyPerHour < 0 || l.deficit },
  ];
  if (l.rp !== undefined) {
    chips.push({ kind: 'rp', icon: '🔬', label: 'Kutatási pont', color: '#c4b5fd', amount: l.rp, rate: l.rpPerHour ?? 0, capacity: null, full: false, empty: false, warn: false });
  }
  return chips;
}

/** Brief highlight when the value moves: growth (rarely), spending (always). Off with reduced motion (CSS). */
function useFlash(value: number): 'up' | 'down' | null {
  const prev = useRef(value);
  const lastUp = useRef(0);
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);
  useEffect(() => {
    const before = prev.current;
    prev.current = value;
    if (value === before) return;
    const now = Date.now();
    if (value > before && now - lastUp.current < 5000) return;
    if (value > before) lastUp.current = now;
    setFlash(value > before ? 'up' : 'down');
    const t = setTimeout(() => setFlash(null), 800);
    return () => clearTimeout(t);
  }, [value]);
  return flash;
}

function Chip({ c, open, panelId, onToggle }: { c: ChipData; open: boolean; panelId: string; onToggle: () => void }) {
  const flash = useFlash(c.amount);
  const pct = c.capacity ? Math.min(100, Math.round((c.amount / c.capacity) * 100)) : 0;
  const nearFull = c.capacity !== null && pct >= 90;
  const state = c.empty ? ' (kifogyott)' : c.full ? ' (tele a raktár)' : '';
  const rateText = c.full ? 'tele' : formatRate(c.rate);
  return (
    <button
      type="button"
      className={`res-chip ${c.warn ? 'res-chip-warn' : ''} ${flash ? `res-flash-${flash}` : ''}`}
      data-testid={`res-${c.kind}`}
      data-amount={c.amount}
      data-rate={c.rate}
      title={`${c.label}: ${formatExact(c.amount)}${c.capacity !== null ? ` / ${formatExact(c.capacity)}` : ''} · ${formatRate(c.rate)}`}
      aria-label={`${c.label}: ${formatExact(c.amount)}, ${formatRate(c.rate)}${state}. Részletek`}
      aria-expanded={open}
      aria-controls={panelId}
      onClick={onToggle}
    >
      <span aria-hidden className="res-icon">{c.icon}</span>
      <span className="res-value" style={{ color: c.empty ? '#f87171' : c.color }}>{formatExact(c.amount)}</span>
      <span className={`res-rate ${c.rate < 0 ? 'res-rate-neg' : c.full ? 'res-rate-full' : ''}`}>{rateText}</span>
      {c.capacity !== null && (
        <span aria-hidden className={`res-meter ${nearFull ? 'res-meter-hot' : ''}`}><span style={{ width: `${pct}%`, background: c.warn ? '#f87171' : c.color }} /></span>
      )}
    </button>
  );
}

function DetailRow({ k, v, tone }: { k: string; v: string; tone?: 'warn' }) {
  return <div className="flex justify-between gap-4"><dt className="text-slate-400">{k}</dt><dd className={`font-mono ${tone === 'warn' ? 'text-red-400' : 'text-slate-100'}`}>{v}</dd></div>;
}

function Details({ c, l }: { c: ChipData; l: LiveResources }) {
  const upkeep = c.kind === 'metal' ? l.metalUpkeepPerHour : c.kind === 'energy' ? l.energyUpkeepPerHour : undefined;
  const fullIn = c.kind === 'metal' ? l.metalFullInH : c.kind === 'energy' ? l.energyFullInH : null;
  const emptyIn = c.kind === 'metal' ? l.metalEmptyInH : c.kind === 'energy' ? l.energyEmptyInH : null;
  return (
    <section aria-label={c.label} data-testid={`res-detail-${c.kind}`}>
      <h3 className="text-xs font-semibold mb-1" style={{ color: c.color }}>{c.icon} {c.label}</h3>
      <dl className="text-xs space-y-0.5">
        <DetailRow k="Készlet" v={formatExact(c.amount)} />
        <DetailRow k="Nettó termelés" v={formatRate(c.rate)} tone={c.rate < 0 ? 'warn' : undefined} />
        {upkeep !== undefined && upkeep > 0 && <DetailRow k="Fenntartás" v={`−${formatCompact(upkeep)}/h`} />}
        {c.capacity !== null && <DetailRow k="Raktár kapacitása" v={formatExact(c.capacity)} />}
        {c.full && <DetailRow k="Raktár" v="tele, a termelés áll" />}
        {fullIn !== null && <DetailRow k="Megtelik" v={`${formatEta(fullIn)} múlva`} />}
        {c.empty && <DetailRow k="Készlet" v="kifogyott" tone="warn" />}
        {emptyIn !== null && <DetailRow k="Kimerül" v={`${formatEta(emptyIn)} múlva`} tone="warn" />}
      </dl>
    </section>
  );
}

export function ResourceBar() {
  const live = useLiveResources();
  const setActiveTab = useGameStore(s => s.setActiveTab);
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [announce, setAnnounce] = useState('');
  const liveRef = useRef(live);
  liveRef.current = live;

  // Screen readers: one calm summary a minute, not a tick per second
  useEffect(() => {
    const say = () => {
      const l = liveRef.current;
      if (l) setAnnounce(`Fém ${formatExact(l.metal)}, energia ${formatExact(l.energy)}${l.rp !== undefined ? `, kutatási pont ${formatExact(l.rp)}` : ''}${l.deficit ? ', energiahiány' : ''}`);
    };
    const first = setTimeout(say, 1500);
    const t = setInterval(say, 60_000);
    return () => { clearTimeout(first); clearInterval(t); };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: Event) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('pointerdown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  if (!live) return <div className="res-bar res-bar-empty" data-testid="resource-bar" aria-hidden />;
  const chips = chipsOf(live);

  return (
    <div ref={rootRef} className="res-bar" data-testid="resource-bar" role="group" aria-label="Készlet">
      <div className="res-row">
        {chips.map(c => <Chip key={c.kind} c={c} open={open} panelId={panelId} onToggle={() => setOpen(o => !o)} />)}
        <span className="res-credits hidden md:inline-flex" title="Kredit" data-testid="res-credits">
          <span aria-hidden>💰</span><span className="font-mono">{formatCompact(live.credits)}</span>
        </span>
        {live.deficit && (
          <span className="res-alert" role="status" data-testid="res-deficit">⚠ <span className="hidden sm:inline">Energiahiány</span></span>
        )}
        <span className="sr-only" role="status" aria-live="polite">{announce}</span>
      </div>
      {open && (
        <div id={panelId} className="res-panel sci-fi-panel" data-testid="res-details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {chips.map(c => <Details key={c.kind} c={c} l={live} />)}
          </div>
          <button type="button" className="res-open-economy" onClick={() => { setOpen(false); setActiveTab('empire'); }}>
            Gazdaság megnyitása
          </button>
        </div>
      )}
    </div>
  );
}
